import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AlertTriangle, ChevronRight, ClipboardList, Loader2, Plus, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { INVENTORY_STATUS_LABELS, inventoryGaps, procurementError } from "../lib/procurement";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Skeleton } from "../components/ui/skeleton";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";

const dayText = (iso) => (iso ? iso.slice(0, 10).split("-").reverse().join("/") : "");
const TONES = { open: "bg-sky-50 text-sky-800 border-sky-200", closed: "bg-emerald-50 text-emerald-800 border-emerald-200", cancelled: "bg-slate-50 text-slate-600 border-slate-200" };

export function InventoryStatusBadge({ status }) {
  return <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium whitespace-nowrap ${TONES[status] || TONES.cancelled}`}>{INVENTORY_STATUS_LABELS[status] || status}</span>;
}

function progress(item) {
  const c = item.counts;
  return c.total ? `${c.total - c.unchecked} / ${c.total} pointés` : "—";
}

function OpenDialog({ onClose }) {
  const navigate = useNavigate();
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/procurement/inventories", { note: note.trim() || null });
      toast.success("Inventaire ouvert");
      navigate(`/achats/inventaire/${data.id}`);
    } catch (e) {
      setError(procurementError(e, "Impossible d'ouvrir l'inventaire."));
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(open) => !open && !busy && onClose()}>
      <DialogContent data-testid="inventory-open-dialog">
        <DialogHeader>
          <DialogTitle>Ouvrir un inventaire</DialogTitle>
          <DialogDescription>
            La liste des actifs en service est figée à cet instant. Vous pointez chacun : trouvé, absent ou ailleurs. Rien ne change sur un actif tant que vous ne
            l'avez pas fait vous-même.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5">
          <Label htmlFor="inventory-note">Note (facultatif)</Label>
          <Input id="inventory-note" value={note} maxLength={300} onChange={(e) => setNote(e.target.value)} placeholder="Inventaire de fin d'année" className="h-10" data-testid="inventory-note" />
        </div>
        {error && <p className="text-sm text-red-600" role="alert" data-testid="inventory-open-error">{error}</p>}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Annuler</Button>
          <Button onClick={submit} disabled={busy} data-testid="inventory-open-confirm">{busy && <Loader2 size={14} className="mr-1.5 animate-spin" />}Ouvrir l'inventaire</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Periodic inventory (Ordre 20-4): one campaign open at a time; the past ones stay readable with their gaps.
export default function ProcurementInventory() {
  const navigate = useNavigate();
  const [data, setData] = useState({ items: [], total: 0, can_manage: false, open_id: null });
  const [phase, setPhase] = useState("loading");
  const [error, setError] = useState("");
  const [opening, setOpening] = useState(false);
  const seq = useRef(0);

  const load = useCallback(async () => {
    const mine = ++seq.current;
    setPhase("loading");
    try {
      const { data: body } = await api.get("/procurement/inventories", { params: { limit: 50 } });
      if (mine !== seq.current) return;
      setData(body);
      setPhase("ready");
    } catch (e) {
      if (mine !== seq.current) return;
      setError(procurementError(e, "Impossible de charger les inventaires."));
      setPhase("error");
    }
  }, []);
  useEffect(() => { load(); return () => { seq.current += 1; }; }, [load]);

  const current = data.items.find((i) => i.id === data.open_id);
  return (
    <div className="space-y-4" data-testid="procurement-inventory-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="font-display font-bold text-2xl text-slate-900">Inventaire des actifs</h1>
          <p className="text-sm text-slate-500">Vérifiez périodiquement que le matériel est là où le registre le dit.</p>
        </div>
        {data.can_manage && !data.open_id && <Button onClick={() => setOpening(true)} data-testid="inventory-new"><Plus size={16} className="mr-1.5" />Ouvrir un inventaire</Button>}
      </div>

      {phase === "loading" && <div className="space-y-2"><Skeleton className="h-12 w-full" /><Skeleton className="h-12 w-full" /></div>}
      {phase === "error" && (
        <Card className="p-6 text-center space-y-3">
          <AlertTriangle className="mx-auto text-amber-500" size={20} />
          <p className="text-sm text-slate-700" data-testid="inventory-error">{error}</p>
          <Button variant="outline" onClick={load}><RefreshCw size={14} className="mr-1.5" />Réessayer</Button>
        </Card>
      )}
      {phase === "ready" && current && (
        <Card className="p-4 flex flex-wrap items-center justify-between gap-3 border-sky-200 bg-sky-50/50" data-testid="inventory-current">
          <div className="min-w-0">
            <div className="font-medium text-slate-900">{current.number} · en cours</div>
            <div className="text-sm text-slate-600">{progress(current)} · ouvert le {dayText(current.opened_at)}</div>
          </div>
          <Button asChild><Link to={`/achats/inventaire/${current.id}`} data-testid="inventory-continue">Continuer le pointage</Link></Button>
        </Card>
      )}
      {phase === "ready" && data.items.length === 0 && (
        <Card className="p-8 text-center space-y-2" data-testid="inventory-empty">
          <ClipboardList className="mx-auto text-slate-300" size={26} />
          <h3 className="font-medium text-slate-900">Aucun inventaire pour le moment</h3>
          <p className="text-sm text-slate-500 max-w-md mx-auto">Ouvrez un inventaire pour pointer chaque actif en service : les écarts apparaissent à la clôture.</p>
          {data.can_manage && <Button size="sm" onClick={() => setOpening(true)}><Plus size={14} className="mr-1.5" />Ouvrir un inventaire</Button>}
        </Card>
      )}
      {phase === "ready" && data.items.length > 0 && (
        <>
          <Card className="hidden md:block overflow-hidden" data-testid="inventory-table">
            <Table>
              <TableHeader><TableRow><TableHead>Inventaire</TableHead><TableHead>Situation</TableHead><TableHead>Pointage</TableHead><TableHead>Écarts</TableHead><TableHead>Ouvert le</TableHead></TableRow></TableHeader>
              <TableBody>
                {data.items.map((i) => (
                  <TableRow key={i.id} className="cursor-pointer" onClick={() => navigate(`/achats/inventaire/${i.id}`)} data-testid={`inventory-row-${i.number}`}>
                    <TableCell><Link to={`/achats/inventaire/${i.id}`} className="font-medium text-slate-900 hover:underline" onClick={(e) => e.stopPropagation()}>{i.number}</Link>{i.note && <div className="text-xs text-slate-500">{i.note}</div>}</TableCell>
                    <TableCell><InventoryStatusBadge status={i.status} /></TableCell>
                    <TableCell className="text-sm tabular-nums">{progress(i)}</TableCell>
                    <TableCell className="text-sm tabular-nums">{i.status === "closed" ? (inventoryGaps(i.counts) ? <span className="text-rose-700 font-medium">{inventoryGaps(i.counts)}</span> : "Aucun") : "—"}</TableCell>
                    <TableCell className="text-sm">{dayText(i.opened_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
          <Card className="md:hidden divide-y divide-slate-100" data-testid="inventory-cards">
            {data.items.map((i) => (
              <Link key={i.id} to={`/achats/inventaire/${i.id}`} className="flex items-start gap-3 px-4 py-3" data-testid={`inventory-card-${i.number}`}>
                <div className="min-w-0 flex-1 space-y-1">
                  <div className="font-medium text-slate-900">{i.number}{i.note ? ` · ${i.note}` : ""}</div>
                  <div className="flex flex-wrap items-center gap-2 text-sm"><InventoryStatusBadge status={i.status} /><span className="text-slate-600 tabular-nums">{progress(i)}</span></div>
                  <div className="text-xs text-slate-500">Ouvert le {dayText(i.opened_at)}{i.status === "closed" ? ` · ${inventoryGaps(i.counts)} écart${inventoryGaps(i.counts) > 1 ? "s" : ""}` : ""}</div>
                </div>
                <ChevronRight size={16} className="text-slate-300 shrink-0 mt-1" />
              </Link>
            ))}
          </Card>
        </>
      )}
      {opening && <OpenDialog onClose={() => setOpening(false)} />}
    </div>
  );
}
